import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

const STATS = [
  { value: 55, suffix: "", label: "KM/H Top Speed", prefix: "" },
  { value: 4.8, suffix: "\u2605", label: "Customer Rating", prefix: "", decimal: true },
  { value: 842, suffix: "+", label: "Happy Racers Reviewed", prefix: "" },
  { value: 250, suffix: "", label: "Starting Price", prefix: "\u20B9" },
];

function StatItem({ value, suffix, prefix, label, decimal, delay }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.5 }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [started]);

  useEffect(() => {
    if (!started) return;

    const duration = 1500;
    const delayMs = delay * 200;
    let startTime = null;
    let frameId;

    const timeout = setTimeout(() => {
      const animate = (now) => {
        if (!startTime) startTime = now;
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = eased * value;
        setCount(decimal ? parseFloat(current.toFixed(1)) : Math.floor(current));
        if (progress < 1) {
          frameId = requestAnimationFrame(animate);
        }
      };
      frameId = requestAnimationFrame(animate);
    }, delayMs);

    return () => {
      clearTimeout(timeout);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [started, value, decimal, delay]);

  return (
    <div ref={ref} className="flex flex-col items-center gap-2 py-8">
      <span className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#f5f5f5]">
        {prefix}{decimal ? count.toFixed(1) : count}{suffix}
      </span>
      <span className="text-[#a3a3a3] text-xs sm:text-sm uppercase tracking-[0.2em] text-center">
        {label}
      </span>
    </div>
  );
}

export default function StatsBar() {
  return (
    <section data-testid="stats-bar" className="relative bg-[#141414] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/5">
          {STATS.map((stat, i) => (
            <motion.div
              key={stat.label}
              data-testid={`stat-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
            >
              <StatItem {...stat} delay={i} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
